import React, { useState, useEffect, useRef, useCallback } from 'react';
import Logo from './Logo';
import './Navbar.css';

/**
 * Section IDs mapped to their visual "theme" for the logo.
 * - 'dark'    → default logo (bright logo on dark bg)
 * - 'lighter' → sections with slightly lighter/textured bg (brushed, panel backgrounds)
 * - 'video'   → hero section with video background
 */
const SECTION_THEMES = {
  home: 'video',
  approach: 'dark',
  services: 'lighter',
  process: 'dark',
  contact: 'dark',
};

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('video');
  const navRef = useRef(null);

  // Detect which section the navbar is currently overlapping
  const detectSection = useCallback(() => {
    if (!navRef.current) return;
    const navBottom = navRef.current.getBoundingClientRect().bottom;

    // Check all sections from bottom to top to find which one the nav overlaps
    const sections = document.querySelectorAll('section[id], .marquee-wrap, .stats-band');
    let found = 'video'; // default

    sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      // If the section's top is above the nav bottom and its bottom is below the nav top
      if (rect.top <= navBottom && rect.bottom > 0) {
        const id = section.id || section.className;
        if (SECTION_THEMES[id]) {
          found = SECTION_THEMES[id];
        } else if (section.classList.contains('marquee-wrap') || section.classList.contains('stats-band')) {
          found = 'lighter';
        }
      }
    });

    setCurrentTheme(found);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      detectSection();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Initial detection
    detectSection();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [detectSection]);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileOpen(false);
    }
  };

  return (
    <header
      ref={navRef}
      className={`navbar ${scrolled ? 'navbar--scrolled' : ''} navbar--theme-${currentTheme}`}
    >
      <nav className="wrap navbar__inner">
        <a
          href="#top"
          className="brand"
          aria-label="adap8 intelligence LLP, home"
          onClick={(e) => handleNavClick(e, '#top')}
        >
          <Logo className="brand__logo" />
        </a>

        <div className={`navlinks ${mobileOpen ? 'navlinks--open' : ''}`}>
          {['approach', 'services', 'process', 'contact'].map((item) => (
            <a
              key={item}
              href={`#${item}`}
              onClick={(e) => handleNavClick(e, `#${item}`)}
            >
              {item.charAt(0).toUpperCase() + item.slice(1)}
            </a>
          ))}
        </div>

        <div className="navcta">
          <a
            href="#contact"
            className="btn btn-primary"
            onClick={(e) => handleNavClick(e, '#contact')}
          >
            Start a conversation
          </a>
        </div>

        <button
          className="navtoggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <span className={`navtoggle__bar ${mobileOpen ? 'open' : ''}`} />
          <span className={`navtoggle__bar ${mobileOpen ? 'open' : ''}`} />
          <span className={`navtoggle__bar ${mobileOpen ? 'open' : ''}`} />
        </button>
      </nav>
    </header>
  );
}

export default Navbar;
